import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message, Modal, Radio, Upload, Alert, Slider } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import { coreApi } from '../../api/calls';

const ORIENTATION_CONFIG = {
  vertical_185x1200: {
    aspectRatio: 185 / 1200, // width / height = 0.1542
    recommendedWidth: 185,
    recommendedHeight: 1200,
    label: 'Vertical 185×1200',
    description: 'Tall vertical banner',
    orientationType: 'vertical',
    restrictPosition: false,
    minZoom: 0.1,
    maxZoom: 5,
    objectFit: "vertical-cover"
  },
  horizontal_620x22: {
    aspectRatio: 620 / 22, // width / height = 28.18
    recommendedWidth: 620,
    recommendedHeight: 22,
    label: 'Horizontal 620×22',
    description: 'With vertical banner (table row height)',
    orientationType: 'horizontal',
    restrictPosition: false,
    minZoom: 0.1,
    maxZoom: 5,
    objectFit: 'horizontal-cover'
  },
};

// Helper: Get image dimensions from file
const getImageDimensionsFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const createImage = (url) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });
};

// Export at 2x the placed size: the PDF places the vertical banner at
// ~4.9cm wide, so a 1x (185px) asset prints at ~96 dpi and looks blurry.
// A 2x PNG (370x2400 / 1240x44) embedded by Dompdf at native resolution
// prints at ~200 dpi — sharp, with no rescaling inside the PDF pipeline.
const EXPORT_SCALE = 2;

/**
 * Crops the selected region and resizes it to an EXACT export size with
 * high-quality resampling. Previously the output size was whatever region
 * of the source happened to be selected (could be tiny when zoomed in),
 * which caused blurry upscaling later in the PDF.
 *
 * Browsers don't expose LANCZOS, so large downscales are done in
 * progressive halving steps with imageSmoothingQuality='high' — visually
 * equivalent for banner content (single-step bilinear is what causes the
 * mush on big ratios).
 */
const getCroppedImg = async (imageSrc, pixelCrop, targetWidth, targetHeight) => {
  const image = await createImage(imageSrc);

  // Step 1: cut the selected region at the source's native resolution
  let current = document.createElement('canvas');
  current.width = Math.max(1, Math.round(pixelCrop.width));
  current.height = Math.max(1, Math.round(pixelCrop.height));
  const srcCtx = current.getContext('2d');

  if (!srcCtx) {
    throw new Error('Could not get canvas context');
  }

  srcCtx.fillStyle = '#FFFFFF';
  srcCtx.fillRect(0, 0, current.width, current.height);
  srcCtx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    current.width,
    current.height
  );

  // Step 2: progressively halve down towards the target (Lanczos-like result)
  while (current.width >= targetWidth * 2 && current.height >= targetHeight * 2) {
    const next = document.createElement('canvas');
    next.width = Math.max(targetWidth, Math.round(current.width / 2));
    next.height = Math.max(targetHeight, Math.round(current.height / 2));
    const stepCtx = next.getContext('2d');
    stepCtx.imageSmoothingEnabled = true;
    stepCtx.imageSmoothingQuality = 'high';
    stepCtx.drawImage(current, 0, 0, next.width, next.height);
    current = next;
  }

  // Step 3: final pass to the exact export dimensions
  const out = document.createElement('canvas');
  out.width = targetWidth;
  out.height = targetHeight;
  const ctx = out.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, targetWidth, targetHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(current, 0, 0, current.width, current.height, 0, 0, targetWidth, targetHeight);

  return new Promise((resolve, reject) => {
    out.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve({ blob, width: targetWidth, height: targetHeight });
      },
      'image/png',
      1.0
    );
  });
};

const getInitialCropRegion = (dimensions, config) => {
  if (!dimensions) return null;

  const { width: imgWidth, height: imgHeight } = dimensions;
  const targetWidth = config.recommendedWidth;
  const targetHeight = config.recommendedHeight;
  const aspect = targetWidth / targetHeight;

  let region;
  let mode;

  if (config.orientationType === 'vertical') {
    if (imgWidth > targetWidth) {
      const regionHeight = imgWidth / aspect;
      region = { x: 0, y: 0, width: imgWidth, height: regionHeight };
      mode = 'vertical:fit-width-top';
    } else {
      region = {
        x: imgWidth - targetWidth,
        y: 0,
        width: targetWidth,
        height: targetHeight,
      };
      mode = 'vertical:right-align-1to1-top';
    }
  } else {
    let regionWidth;
    let regionHeight;
    if (imgWidth / imgHeight > aspect) {
      regionHeight = imgHeight;
      regionWidth = imgHeight * aspect;
    } else {
      regionWidth = imgWidth;
      regionHeight = imgWidth / aspect;
    }
    region = {
      x: (imgWidth - regionWidth) / 2,
      y: (imgHeight - regionHeight) / 2,
      width: regionWidth,
      height: regionHeight,
    };
    mode = 'horizontal:cover';
  }

  return region;
};

const getZoomForRegion = (dimensions, region, config) => {
  if (!dimensions || !region) return config.minZoom;
  return config.orientationType === 'vertical'
    ? dimensions.height / region.height
    : dimensions.width / region.width;
};

const BannerUpload = ({ campaignId, onUploadSuccess }) => {
  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [bannerOrientationType, setBannerOrientationType] = useState('vertical_185x1200');
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedArea, setCroppedArea] = useState(null);
  const [exportDimensions, setExportDimensions] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [cropKey, setCropKey] = useState(0);

  const currentConfig = ORIENTATION_CONFIG[bannerOrientationType];

  const initialCropRegion = useMemo(
    () => getInitialCropRegion(imageDimensions, currentConfig),
    [imageDimensions, currentConfig]
  );
  const minZoom = useMemo(() => {
    const fitZoom = getZoomForRegion(imageDimensions, initialCropRegion, currentConfig);
    return Math.min(currentConfig.minZoom, fitZoom);
  }, [imageDimensions, initialCropRegion, currentConfig]);

  const handleFileSelect = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(t('campaigns.invalidFileType'));
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error(t('campaigns.fileTooLarge'));
      return false;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      message.error(t('campaigns.invalidFileFormat'));
      return false;
    }

    setValidationError(null);

    try {
      const dimensions = await getImageDimensionsFromFile(file);
      setImageDimensions(dimensions);
    } catch (error) {
      console.error('Failed to get image dimensions:', error);
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
      setOriginalFile(file);
      setCroppedAreaPixels(null);
      setCroppedArea(null);
      setExportDimensions(null);
      setCropKey((k) => k + 1);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const onCropComplete = useCallback((croppedAreaPercent, croppedAreaPixels) => {
    setCroppedArea(croppedAreaPercent);
    setCroppedAreaPixels(croppedAreaPixels);

    // Show actual export dimensions
    const { width: cropWidth, height: cropHeight } = croppedAreaPixels;
    setExportDimensions({
      width: Math.round(cropWidth),
      height: Math.round(cropHeight)
    });
  }, []);

  const handleOrientationChange = (e) => {
    setBannerOrientationType(e.target.value);
    // Remount the cropper so the initial crop is recomputed for the new aspect ratio.
    setCropKey((k) => k + 1);
  };

  const handleAlignTop = () => {
    if (!imageDimensions || !croppedArea) return;

    if (currentConfig.orientationType === 'vertical') {
      const currentCropAreaY = croppedArea.y;
      const targetCropAreaY = 100 - croppedArea.height;
      const adjustment = targetCropAreaY - currentCropAreaY;

      setCrop(prev => {
        const newY = prev.y + adjustment;
        return { ...prev, y: newY };
      });
    } else {
      // For horizontal banners: move image to LEFT
      const currentCropAreaX = croppedArea.x;
      const targetCropAreaX = 100 - croppedArea.width;
      const adjustment = targetCropAreaX - currentCropAreaX;

      setCrop(prev => {
        const newX = prev.x + adjustment;
        return { ...prev, x: newX };
      });
    }
  };

  const handleAlignCenter = () => {
    if (!imageDimensions) return;

    setCrop({ x: 0, y: 0 });
  };

  const handleAlignBottom = () => {
    if (!imageDimensions || !croppedArea) return;

    if (currentConfig.orientationType === 'vertical') {
      const currentCropAreaY = croppedArea.y;
      const targetCropAreaY = 0;
      const adjustment = targetCropAreaY - currentCropAreaY;

      setCrop(prev => {
        const newY = prev.y + adjustment;
        return { ...prev, y: newY };
      });
    } else {
      // For horizontal banners: move image to RIGHT
      const currentCropAreaX = croppedArea.x;
      const targetCropAreaX = 0;
      const adjustment = targetCropAreaX - currentCropAreaX;

      setCrop(prev => {
        const newX = prev.x + adjustment;
        return { ...prev, x: newX };
      });
    }
  };

  const handleUpload = async () => {
    if (!previewImage || !croppedAreaPixels) {
      message.warning(t('campaigns.selectImageFirst'));
      return;
    }

    setUploading(true);
    setValidationError(null);

    try {
      const exportWidth = currentConfig.recommendedWidth * EXPORT_SCALE;
      const exportHeight = currentConfig.recommendedHeight * EXPORT_SCALE;
      const { blob } = await getCroppedImg(previewImage, croppedAreaPixels, exportWidth, exportHeight);

      // The blob is a PNG — the filename must say so too. Keeping the
      // original .jpg name made the PDF template emit "data:image/jpg"
      // and let the backend re-encode the PNG back into lossy JPEG.
      const baseName = originalFile.name.replace(/\.[^.]+$/, '');
      const croppedFile = new File([blob], `${baseName}.png`, {
        type: 'image/png',
      });

      const formData = new FormData();
      formData.append('file', croppedFile);
      formData.append('banner_orientation_type', currentConfig.orientationType);

      await coreApi.post(`/campaigns/${campaignId}/banners`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      message.success(t('campaigns.bannerUploadSuccess'));
      handleModalClose();
      onUploadSuccess();
    } catch (error) {
      console.error(error);

      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors) {
          const errorMessages = Object.entries(errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          setValidationError(errorMessages);
        } else {
          setValidationError(error.response.data.message);
        }
      } else if (error.response?.status === 400) {
        setValidationError(error.response.data.message);
      } else {
        message.error(error.response?.data?.message || t('campaigns.bannerUploadError'));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setPreviewImage(null);
    setOriginalFile(null);
    setValidationError(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCroppedArea(null);
    setExportDimensions(null);
    setImageDimensions(null);
  };

  const isBelowRecommended =
    exportDimensions &&
    (exportDimensions.width < currentConfig.recommendedWidth ||
      exportDimensions.height < currentConfig.recommendedHeight);

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setModalOpen(true)}
      >
        {t('campaigns.addBanner')}
      </Button>

      <Modal
        title={t('campaigns.uploadBanner')}
        open={isModalOpen}
        onCancel={handleModalClose}
        width={900}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploading}
            onClick={handleUpload}
            disabled={!previewImage || !croppedAreaPixels}
            icon={<UploadOutlined />}
          >
            {t('campaigns.uploadButton')}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {validationError && (
            <Alert
              message={t('campaigns.validationError')}
              description={validationError}
              type="error"
              closable
              onClose={() => setValidationError(null)}
              style={{ whiteSpace: 'pre-line' }}
            />
          )}

          {/* Step 1: File Upload */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              1. {t('campaigns.selectImage')}:
            </div>
            <Upload
              beforeUpload={handleFileSelect}
              showUploadList={false}
              accept="image/jpeg,image/jpg,image/png"
            >
              <Button icon={<UploadOutlined />} block>
                {previewImage ? t('campaigns.changeImage') : t('campaigns.selectImageButton')}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              {t('campaigns.supportedFormats')}
            </div>
          </div>

          {/* Step 2: Banner Orientation */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              2. {t('campaigns.bannerOrientation')}:
            </div>
            <Radio.Group
              value={bannerOrientationType}
              onChange={handleOrientationChange}
              style={{ width: '100%' }}
            >
              <div style={{ display: 'flex', gap: 24 }}>
                {/* Vertical banners column */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8, color: '#1890ff' }}>
                    {t('campaigns.verticalBanners')}
                  </div>
                  {Object.entries(ORIENTATION_CONFIG)
                    .filter(([, config]) => config.orientationType === 'vertical')
                    .map(([key, config]) => (
                      <Radio key={key} value={key} style={{ display: 'block', marginBottom: 8 }}>
                        {config.label}
                      </Radio>
                    ))}
                </div>

                {/* Horizontal banners column */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8, color: '#1890ff' }}>
                    {t('campaigns.horizontalBanners')}
                  </div>
                  {Object.entries(ORIENTATION_CONFIG)
                    .filter(([, config]) => config.orientationType === 'horizontal')
                    .map(([key, config]) => (
                      <Radio key={key} value={key} style={{ display: 'block', marginBottom: 8 }}>
                        {config.label}
                      </Radio>
                    ))}
                </div>
              </div>
            </Radio.Group>
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              {t('campaigns.recommendedSizeLabel')}: {currentConfig.recommendedWidth}×{currentConfig.recommendedHeight}px
            </div>
          </div>

          {/* Step 3: Crop Area */}
          {previewImage && (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                3. {t('campaigns.cropArea')}:
              </div>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 400,
                  backgroundColor: '#000',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <Cropper
                  key={`${bannerOrientationType}-${cropKey}`}
                  image={previewImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={currentConfig.aspectRatio}
                  initialCroppedAreaPixels={initialCropRegion}
                  onCropChange={setCrop}
                  onZoomChange={(z) => {
                    setZoom(z);
                  }}
                  onCropComplete={onCropComplete}
                  restrictPosition={currentConfig.restrictPosition}
                  minZoom={minZoom}
                  maxZoom={currentConfig.maxZoom}
                  objectFit={currentConfig.objectFit}
                />
              </div>

              {/* Zoom Slider */}
              <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 500 }}>
                  {t('campaigns.zoom')}:
                </div>
                <Slider
                  min={minZoom}
                  max={currentConfig.maxZoom}
                  step={0.01}
                  value={zoom}
                  onChange={setZoom}
                />
              </div>

              {/* Position and Reset Buttons */}
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  onClick={() => setCropKey((k) => k + 1)}
                  size="small"
                >
                  {t('campaigns.resetPositionAndZoom')}
                </Button>
                {currentConfig.orientationType === 'vertical' && (
                  <Button
                    onClick={handleAlignTop}
                    size="small"
                    disabled={!imageDimensions}
                  >
                    ↑ {t('campaigns.alignTop')}
                  </Button>
                )}
                <Button
                  onClick={handleAlignCenter}
                  size="small"
                  disabled={!imageDimensions}
                >
                  ↔ {t('campaigns.alignCenter')}
                </Button>
                {currentConfig.orientationType === 'vertical' && (
                  <Button
                    onClick={handleAlignBottom}
                    size="small"
                    disabled={!imageDimensions}
                  >
                    ↓ {t('campaigns.alignBottom')}
                  </Button>
                )}
              </div>

              {/* Helper Text */}
              <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                <div>
                  <strong>{t('campaigns.recommendedSize')}:</strong> {currentConfig.recommendedWidth}×
                  {currentConfig.recommendedHeight}px
                </div>
                {exportDimensions && (
                  <div>
                    <strong>{t('campaigns.cropSize')}:</strong> {exportDimensions.width}×
                    {exportDimensions.height}px
                    {' '}→ {currentConfig.recommendedWidth * EXPORT_SCALE}×
                    {currentConfig.recommendedHeight * EXPORT_SCALE}px PNG
                  </div>
                )}
                <div style={{ marginTop: 4, color: '#999' }}>
                  {t('campaigns.imageSavedInSelectedResolution')}
                </div>
              </div>

              {/* Warning for small images */}
              {isBelowRecommended && (
                <Alert
                  message={t('campaigns.cropAreaSmallerWarningTitle')}
                  description={t('campaigns.cropAreaSmallerWarningDescription', {
                    width: currentConfig.recommendedWidth,
                    height: currentConfig.recommendedHeight
                  })}
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default BannerUpload;